import { IIncentiveCriterion, ITableItem } from "../components/Shared/ZeosTable/Interfaces";
import type { IAiSummary } from "../services/zeos-api/contracts/Incentive";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const escapeHtml = (value?: string) =>
  (value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// 4038: the program-link hover panel is a glance, not the detail page. Trim long
// prose at a word boundary so the tooltip stays a few lines instead of a wall of text.
const truncateText = (value: string | undefined, maxLength: number) => {
  const text = (value || "").trim();
  if (text.length <= maxLength) {
    return text;
  }

  const clipped = text.slice(0, maxLength);
  const lastSpace = clipped.lastIndexOf(" ");
  return `${(lastSpace > maxLength * 0.6 ? clipped.slice(0, lastSpace) : clipped).replace(
    /[\s,;:.-]+$/,
    ""
  )}…`;
};

const HOVER_SUMMARY_MAX_CHARS = 180;
const HOVER_BULLET_MAX_CHARS = 90;
const HOVER_BULLET_LIMIT = 2;
const HOVER_REASON_MAX_CHARS = 110;
const HOVER_CRITERIA_LIMIT = 3;

const positiveAmount = (value?: number | null) =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : undefined;

const inferPositiveAmountFromText = (summary?: IAiSummary) => {
  const text = [summary?.benefitRange?.displayText, summary?.shortText, summary?.longText]
    .filter(Boolean)
    .join(" ");
  const matches = [...text.matchAll(/\$\s*([0-9][0-9,]*(?:\.[0-9]+)?)\s*(k|m|million)?/gi)];
  const amounts = matches
    .map((match) => {
      const parsed = Number(match[1].replace(/,/g, ""));
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return undefined;
      }

      const suffix = match[2]?.toLowerCase();
      if (suffix === "m" || suffix === "million") {
        return parsed * 1000000;
      }
      if (suffix === "k") {
        return parsed * 1000;
      }
      return parsed;
    })
    .filter((amount): amount is number => amount !== undefined);

  return amounts.length > 0 ? Math.max(...amounts) : undefined;
};

const usableDisplayText = (summary?: IAiSummary) => {
  const displayText = summary?.benefitRange?.displayText?.trim();
  if (
    displayText &&
    displayText.length <= 70 &&
    /\$/.test(displayText) &&
    !/\$0(?:\D|$)/.test(displayText) &&
    !/scope required/i.test(displayText)
  ) {
    return displayText;
  }

  return undefined;
};

export const formatExpectedBenefitRange = (summary?: IAiSummary) => {
  const existingDisplayText = usableDisplayText(summary);
  if (existingDisplayText) {
    return existingDisplayText;
  }

  const low = positiveAmount(summary?.benefitRange?.low);
  const high =
    positiveAmount(summary?.benefitRange?.high) ??
    positiveAmount(summary?.upperBound) ??
    inferPositiveAmountFromText(summary);
  const isLowConfidenceEstimate = summary?.benefitRange?.confidence === "Low";

  if (low !== undefined && high !== undefined && low < high) {
    const prefix = isLowConfidenceEstimate ? "Estimated " : "";
    return `${prefix}${currencyFormatter.format(low)} - ${currencyFormatter.format(high)}`;
  }

  if (high !== undefined) {
    return isLowConfidenceEstimate
      ? `Estimated up to ${currencyFormatter.format(high)}`
      : `Up to ${currencyFormatter.format(high)}`;
  }

  if (low !== undefined) {
    return isLowConfidenceEstimate
      ? `Estimated at least ${currencyFormatter.format(low)}`
      : `At least ${currencyFormatter.format(low)}`;
  }

  return "Estimated $5,000 - $25,000";
};

const DETAIL_SURFACE_STYLE = `
  visibility: hidden;
  background-color: #ffffff;
  color: #000000;
  text-align: left;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  border-left: 4px solid #f58921;
  padding: 0.75rem 0.875rem;
  position: absolute;
  z-index: 20;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  min-width: 300px;
  max-width: 420px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  font-size: 0.875rem;
  line-height: 1.45;
  white-space: normal;
`;

const buildBenefitTooltipContent = (summary?: IAiSummary) => {
  const bullets = summary?.bullets?.filter(Boolean) ?? [];
  const confidence = summary?.benefitRange?.confidence;
  const basis = summary?.benefitRange?.basis;

  return `
    <div style="font-weight: 700; color: #000000; margin-bottom: 0.35rem;">Expected Range of Benefit</div>
    <div style="height: 2px; width: 1.5rem; background: #f58921; margin-bottom: 0.5rem;"></div>
    ${
      summary?.longText
        ? `<div style="color: #3f3f3f;">${escapeHtml(summary.longText)}</div>`
        : summary?.shortText
        ? `<div style="color: #3f3f3f;">${escapeHtml(summary.shortText)}</div>`
        : `<div style="color: #3f3f3f;">Final value depends on verified scope, eligibility, and current program terms.</div>`
    }
    ${
      basis
        ? `<div style="margin-top: 0.5rem; font-size: 0.75rem; color: #6f6b6b;">${escapeHtml(
            basis
          )}</div>`
        : ""
    }
    ${
      confidence
        ? `<div style="margin-top: 0.35rem; font-size: 0.75rem; color: #6f6b6b;">
            <span style="display: inline-block; border-radius: 9999px; background: #dbeafe; color: #4053a3; padding: 0.1rem 0.45rem; font-weight: 600;">
              Confidence: ${escapeHtml(confidence)}
            </span>
          </div>`
        : ""
    }
    ${
      bullets.length > 0
        ? `<ul style="margin: 0.5rem 0 0; padding-left: 1.1rem; color: #3f3f3f;">${bullets
            .slice(0, 3)
            .map(
              (b) =>
                `<li style="margin-bottom: 0.2rem; font-size: 0.8125rem;">${escapeHtml(
                  b
                )}</li>`
            )
            .join("")}</ul>`
        : ""
    }
  `;
};

export const renderAiSummary = (summary?: IAiSummary, rowKey?: string | number) => {
  const displayText = formatExpectedBenefitRange(summary);
  const tooltipId = `economic-benefit-tooltip-${String(rowKey ?? "row")
    .replace(/[^a-zA-Z0-9_-]/g, "-")}`;

  return `
    <div style="position: relative; display: inline-block;">
      <button
        type="button"
        aria-describedby="${tooltipId}"
        style="color: #f58921; font: inherit; font-weight: inherit; text-decoration: none; cursor: help; background: transparent; border: 0; padding: 0; text-align: left;"
        onmouseover="document.getElementById('${tooltipId}').style.visibility='visible';"
        onmouseout="document.getElementById('${tooltipId}').style.visibility='hidden';"
        onfocus="document.getElementById('${tooltipId}').style.visibility='visible';"
        onblur="document.getElementById('${tooltipId}').style.visibility='hidden';"
      >
        ${escapeHtml(displayText)}
      </button>
      <div id="${tooltipId}" style="${DETAIL_SURFACE_STYLE}">
        ${buildBenefitTooltipContent(summary)}
      </div>
    </div>
  `;
};

export const renderIncentiveLinkCell = (
  data: {
    url?: string;
    name?: string;
    reasons?: IIncentiveCriterion[];
    aiSummary?: IAiSummary;
  },
  rowKey?: string | number
) => {
  const reasons = data.reasons;
  const aiSummary = data.aiSummary;

  const aiBullets = aiSummary?.bullets?.filter(Boolean) ?? [];
  // 4038: show one trimmed line of prose rather than shortText and longText stacked.
  const aiLead = truncateText(
    aiSummary?.shortText || aiSummary?.longText,
    HOVER_SUMMARY_MAX_CHARS
  );
  const aiTooltipContent =
    aiSummary && (aiLead || aiBullets.length > 0)
      ? `
          <div>
            <div style="font-weight: 700; color: #000000; margin-bottom: 0.35rem;">AI Summary</div>
            <div style="height: 2px; width: 1.5rem; background: #f58921; margin-bottom: 0.5rem;"></div>
            ${
              aiLead
                ? `<div style="color: #3f3f3f; font-weight: 600;">${escapeHtml(
                    aiLead
                  )}</div>`
                : ""
            }
            ${
              aiBullets.length > 0
                ? `<ul style="margin: 0.5rem 0 0; padding-left: 1.1rem; color: #3f3f3f;">${aiBullets
                    .slice(0, HOVER_BULLET_LIMIT)
                    .map(
                      (bullet) =>
                        `<li style="margin-bottom: 0.2rem;">${escapeHtml(
                          truncateText(bullet, HOVER_BULLET_MAX_CHARS)
                        )}</li>`
                    )
                    .join("")}</ul>`
                : ""
            }
          </div>
        `
      : "";

  // 4038: cap the criteria list and trim each reason; the detail page carries the full set.
  const shownReasons = reasons?.slice(0, HOVER_CRITERIA_LIMIT) ?? [];
  const hiddenReasonCount = (reasons?.length ?? 0) - shownReasons.length;
  const criteriaTooltipContent =
    shownReasons.length > 0
      ? `
          <div>
            <div style="font-weight: 700; color: #000000; margin-bottom: 0.35rem;">Match Criteria</div>
            <div style="height: 2px; width: 1.5rem; background: #f58921; margin-bottom: 0.5rem;"></div>
            ${shownReasons
              .map((c) => {
                const reason = truncateText(c.reason, HOVER_REASON_MAX_CHARS);
                return `
                  <div style="margin-bottom: 0.5rem;">
                    <div style="font-weight: 600; color: #f58921;">${escapeHtml(
                      c.criterion
                    )}</div>
                    ${
                      reason
                        ? `<div style="margin-top: 0.15rem; color: #3f3f3f;">${escapeHtml(
                            reason
                          )}</div>`
                        : ""
                    }
                  </div>
                `;
              })
              .join("")}
            ${
              hiddenReasonCount > 0
                ? `<div style="font-size: 0.75rem; color: #6f6b6b;">+${hiddenReasonCount} more — open the program for the full list</div>`
                : ""
            }
          </div>
        `
      : "";

  const tooltipContent =
    aiTooltipContent ||
    criteriaTooltipContent ||
    `<div style="color: #6f6b6b;">No AI summary or matching criteria available yet</div>`;

  const matchSummary =
    aiSummary?.shortText
      ? escapeHtml(aiSummary.shortText)
      : reasons && reasons.length > 0
      ? `Matched: ${escapeHtml(reasons[0].criterion)} — ${escapeHtml(reasons[0].reason)}${
          reasons.length > 1 ? ` (+${reasons.length - 1} more)` : ""
        }`
      : "Matched: eligibility criteria";

  const safeName = (data.name || "incentive").replace(/\s+/g, "-");
  const tooltipId = `tooltip-${safeName}-${rowKey ?? "row"}`;

  return `
    <div style="position: relative; display: inline-block; max-width: 100%;">
      <a href="${data.url || "#"}"
         target="_blank"
         rel="noopener noreferrer"
         style="color: #f58921; text-decoration: underline; cursor: pointer; font-weight: 600;"
         onmouseover="document.getElementById('${tooltipId}').style.visibility='visible';"
         onmouseout="document.getElementById('${tooltipId}').style.visibility='hidden';">
        ${escapeHtml(data.name || "")}
      </a>
      <div style="margin-top: 0.25rem; font-size: 0.75rem; color: #6f6b6b; line-height: 1.35;">${matchSummary}</div>
      <div id="${tooltipId}" style="${DETAIL_SURFACE_STYLE}">
        ${tooltipContent}
      </div>
    </div>
  `;
};

export const incentivesDataMapper = (unmappedData: ITableItem[]) =>
  unmappedData.map((item) => [
    item.key,
    item.category,
    item.area,
    {
      url: item.incentive.url,
      name: item.incentive.urlLabel,
      reasons: item.reasons,
      aiSummary: item.aiSummary,
    },
    item.type,
    item.deadline,
    item.provider,
    item.aiSummary,
  ]);

export const incentivesColumns = [
  { title: "key", key: "key", visible: false, orderable: false },
  { title: "category", key: "category", orderable: false, filterable: true },
  { title: "area", key: "area", orderable: false, filterable: false },
  {
    title: "incentive",
    key: "incentive",
    orderable: false,
    filterable: false,
    render: (data: any, _: any, row: any) =>
      renderIncentiveLinkCell(data, Array.isArray(row) ? row[0] : row?.key),
  },
  { title: "type", key: "type", orderable: false },
  { title: "deadline", key: "deadline", orderable: false },
  { title: "provider", key: "provider", orderable: false },
  {
    title: "Economic Benefit",
    key: "aiSummary",
    orderable: false,
    filterable: false,
    render: (data: IAiSummary | undefined, _: any, row: any) =>
      renderAiSummary(data as IAiSummary, Array.isArray(row) ? row[0] : row?.key),
  },
];
